import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Menu, UnstyledButton, Group, Text, Box, Loader } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { ChevronDown, Check } from 'react-feather';
import { participantPortalApi } from '../services/api/participantPortal.js';
import type { ParticipantCohortListItem } from '../services/api/participantPortal.js';
import { useParticipantCohortStore } from '../stores/participantCohort.js';
import classes from './PersonaSwitcher.module.css';

export function CohortSwitcher() {
  const { selectedCohortId, setSelectedCohortId, setCohorts: setStoreCohorts } = useParticipantCohortStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cohorts, setCohorts] = useState<ParticipantCohortListItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchCohorts = async () => {
      try {
        const result = await participantPortalApi.getCohorts();
        if (cancelled) return;
        setCohorts(result);
        setStoreCohorts(result); // MED-778: sync to global store for feature toggle checks

        // MED-688: honor `?cohort=` URL param from email deep links.
        // Takes precedence over any persisted selection so the dashboard
        // renders the cohort context the email was for.
        const cohortParam = searchParams.get('cohort');
        if (cohortParam) {
          const matched = result.find(c => c.id === cohortParam);
          if (matched) {
            setSelectedCohortId(matched.id);
          } else {
            // Do NOT silently fallback — surface the error so the participant
            // understands why they're not seeing the session they expected.
            notifications.show({
              title: 'Cohort not available',
              message:
                'The cohort referenced by your link is not available for your account. Showing your default program instead.',
              color: 'red',
              autoClose: 8000,
            });
            if (result.length > 0) {
              const validSelection =
                selectedCohortId && result.some(c => c.id === selectedCohortId);
              if (!validSelection) {
                setSelectedCohortId(result[0].id);
              }
            }
          }
          // Clear the param either way so it doesn't re-trigger on navigation
          const next = new URLSearchParams(searchParams);
          next.delete('cohort');
          setSearchParams(next, { replace: true });
        } else if (result.length > 0) {
          // Default to first cohort if none selected or selected no longer valid
          const validSelection = selectedCohortId && result.some(c => c.id === selectedCohortId);
          if (!validSelection) {
            setSelectedCohortId(result[0].id);
          }
        }
      } catch (error) {
        console.error('Failed to fetch participant cohorts:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchCohorts();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <UnstyledButton className={classes.switcher} disabled>
        <Group gap="xs">
          <Loader size={12} color="white" />
        </Group>
      </UnstyledButton>
    );
  }

  if (cohorts.length === 0) return null;

  const selected = cohorts.find(c => c.id === selectedCohortId) ?? cohorts[0];

  const handleSelect = (cohortId: string) => {
    setSelectedCohortId(cohortId);
  };

  return (
    <Menu shadow="md" width={260} position="bottom-start">
      <Menu.Target>
        <UnstyledButton className={classes.switcher}>
          <Group gap="xs">
            <Text size="sm" fw={500} c="white" style={{ maxWidth: 180 }} truncate>
              {selected.name}
            </Text>
            <ChevronDown size={14} className={classes.chevron} />
          </Group>
        </UnstyledButton>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>Switch Cohort</Menu.Label>
        {cohorts.map(cohort => (
          <Menu.Item
            key={cohort.id}
            onClick={() => handleSelect(cohort.id)}
            rightSection={cohort.id === selected.id ? <Check size={14} /> : undefined}
            className={cohort.id === selected.id ? classes.activeItem : undefined}
          >
            <Box>
              <Text size="sm" fw={500}>
                {cohort.name}
              </Text>
              {cohort.partner_name && (
                <Text size="xs" c="dimmed">
                  {cohort.partner_name}
                </Text>
              )}
            </Box>
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

export default CohortSwitcher;
